import { Outlet, useMatches } from 'react-router';
import { getHeaderConfig } from '~/lib/header-config';
import { useRootData } from '~/root';
import { Header } from '~/ui/app-shell/header';
import { Route } from '../+types/root';
import { Footer } from '~/ui/app-shell/footer';
import {
  isCookieConsentEnabled,
  openPanel as openCookieSettings,
} from '~/domain/plugins/services/cookie-consent';
import { css } from '@understory-io/leaf/css';

const headerWrapperBase = css({
  background: { base: 'surface.base' },
});

const headerWrapperRaised = css({
  background: { base: 'surface.raised', tablet: 'surface.base' },
});

export default function AppLayout({ params }: Route.ComponentProps) {
  const storefront = useRootData();
  const matches = useMatches();

  const { companyName, companyWebsite, companyLogo, preferences } = storefront;

  const headerConfig = getHeaderConfig(matches, params.locale, {
    hasMultipleListedExperiences: storefront.listedExperiencesCount > 1,
  });

  const headerFeatures = {
    giftCards: preferences.giftCards,
    punchcards: preferences.punchcards,
    privateEventRequest: preferences.privateEventRequest,
  };

  // Per-route mobile header surface. Routes set this via their handle (see
  // `HeaderConfig.surface` in `~/lib/header-config`). Tablet+ always uses
  // `surface.base` so the visual treatment only diverges on mobile. The
  // two classes are declared statically so Panda can extract them at
  // build time; we pick between them at runtime.
  const headerWrapperClass =
    headerConfig.surface === 'raised' ? headerWrapperRaised : headerWrapperBase;

  return (
    <div
      className={css({
        // A growing column between the header and the footer, so a page shorter
        // than the viewport still puts the footer at the bottom rather than
        // leaving it partway up. `Page` is already a full-height flex column;
        // without these the whole layout only stood as tall as its content and
        // `PageCard`'s `flex: 1` had nothing to grow inside.
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        background: { base: 'surface.base', tablet: 'surface.background' },
      })}
    >
      <div className={headerWrapperClass}>
        <Header
          companyName={companyName}
          companyWebsite={companyWebsite}
          logo={companyLogo}
          config={headerConfig}
          features={headerFeatures}
          locale={params.locale}
          availableLanguages={storefront.languages}
          resolvedLocale={storefront.locale}
          defaultLanguage={storefront.defaultLanguage}
        />
      </div>
      <Outlet />
      <Footer
        layout="default"
        companyName={storefront.companyName}
        companyEmail={storefront.companyEmail}
        companyPhone={storefront.companyPhone}
        showPoweredByUnderstory={storefront.preferences.platformBrandingEnabled}
        showCookieSettings={isCookieConsentEnabled({
          preview: storefront.preview,
        })}
        onCookieSettingsClick={openCookieSettings}
      />
    </div>
  );
}
